import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import ExportPackCard from "@/components/exportaudio/ExportPackCard";
import { EXPORT_ACTIVE, deleteExport, listExports } from "@/lib/exportAudioApi";
import { safeApiError } from "@/lib/safeErrorText";

const POLL_MS = 2500;

/** Your export packs, newest first — progress while one is being made, then the zip + every file. */
export default function ExportPacks({ songId, tick }) {
  const [rows, setRows] = useState(null);
  const load = useCallback(() => listExports().then(setRows).catch(() => setRows([])), []);
  useEffect(() => { load(); }, [load, tick]);
  const busy = Boolean(rows?.some((r) => EXPORT_ACTIVE.includes(r.status)));
  useEffect(() => {
    if (!busy) return undefined;
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [busy, load]);
  const [doomed, setDoomed] = useState(null);
  const remove = async () => {
    try { await deleteExport(doomed.id); toast.success("Export removed"); load(); }
    catch (e) { toast.error("Couldn't remove it", { description: safeApiError(e) }); }
    finally { setDoomed(null); }
  };
  return (
    <section data-testid="export-packs" className="rounded-3xl border border-white/[0.08] bg-[#0a0d1c]/80 p-5 sm:p-6">
      <h2 className="font-display text-base font-semibold md:text-lg">Your export packs</h2>
      <p className="mt-1 text-[12px] text-white/45">Packs are kept for 24 hours (your newest three) — the song itself always stays in your library.</p>
      {rows === null ? <Loader2 className="mt-4 h-5 w-5 animate-spin text-white/40" /> : rows.length === 0 ? (
        <p data-testid="export-packs-empty" className="mt-3 text-[13px] text-white/50">Nothing exported yet.</p>
      ) : (
        <ul className="mt-4 space-y-3">
          {rows.map((r) => <ExportPackCard key={r.id} pack={r} current={r.song_id === songId} onRemove={() => setDoomed(r)} />)}
        </ul>
      )}
      <AlertDialog open={Boolean(doomed)} onOpenChange={(o) => !o && setDoomed(null)}>
        <AlertDialogContent className="border-white/10 bg-[#0b0e1c] text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this export pack?</AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">The zip for “{doomed?.title}” is deleted. The song and its parts stay in your library — you can export again any time.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="export-remove-cancel" className="border-white/15 bg-transparent text-white hover:bg-white/10">Keep it</AlertDialogCancel>
            <AlertDialogAction data-testid="export-remove-confirm" onClick={remove} className="bg-rose-500 text-white hover:bg-rose-400">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
